/**
 * Busy indicator.
 *
 * The prototype had no loading states — it rendered seed data synchronously — so this
 * is new, built from existing tokens only: a --line-soft ring with a --violet arc.
 * It respects `prefers-reduced-motion` and the `.no-animations` class visual tests set.
 *
 * Prefer skeletons for whole-page loads; this is for buttons, panels and refreshes.
 */
import type { CSSProperties } from "react";
import "./ui.css";

export interface SpinnerProps {
  /** Outer diameter in px. */
  size?: number;
  /** Ring thickness in px. */
  thickness?: number;
  /** Text shown beside the ring; also becomes the accessible label. */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

export function Spinner({ size = 16, thickness = 2, label, className, style }: SpinnerProps) {
  const ring = (
    <span
      className={["ui-spinner", label ? null : className].filter(Boolean).join(" ")}
      style={{ width: size, height: size, borderWidth: thickness, ...(label ? undefined : style) }}
      aria-hidden="true"
    />
  );

  if (!label) {
    return (
      <span role="status" aria-label="Loading">
        {ring}
      </span>
    );
  }

  return (
    <span
      className={["ui-spinner__row", className].filter(Boolean).join(" ")}
      style={style}
      role="status"
    >
      {ring}
      {label}
    </span>
  );
}

export default Spinner;
